import type { Observable } from 'rxjs';
import type { PagedList } from '../data/models/paged-list.model';
import type { SortModel } from '../data/models/sort-model.model';
import type { UserRole } from '../data/models/user-role.model';
import type { User } from './../data/models/user.model';

import httpService from './httpService';

class UserService {
  private readonly apiBase;

  constructor() {
    const { __environment__ } = window as unknown as {
      __environment__: { REACT_APP_USER_API_URL: string };
    };
    this.apiBase = __environment__.REACT_APP_USER_API_URL.trim();
  }

  public validateEmailUsername(key: string, data: string): Observable<any> {
    const url = `${this.apiBase}/validate-unique?${key}=${encodeURIComponent(
      data,
    )}`;
    return httpService.getJSON(url);
  }

  public getUserInfo(userid?: string): Observable<any> {
    const id = userid || 'me';
    const url = `${this.apiBase}/${id}`;
    return httpService.getJSON(url);
  }

  public getEULAInfo(applicationTitleID: number): Observable<any> {
    const url = `${this.apiBase}/eula?applicationTitleID=${applicationTitleID}`;
    return httpService.getJSON(url);
  }

  public setEulaFlag(userId: number, version: number): Observable<boolean> {
    const url = `${this.apiBase}/${userId}`;
    return httpService.putJSON(url, {
      agreedToEULA: version,
    });
  }

  public getExternalSupportAgreementInfo(
    applicationTitleID: number,
  ): Observable<any> {
    const url = `${this.apiBase}/external-support-agreement?applicationTitleID=${applicationTitleID}`;
    return httpService.getJSON(url);
  }

  public setExternalSupportAgreementFlag(
    userId: number,
    version: number,
  ): Observable<boolean> {
    const url = `${this.apiBase}/${userId}`;
    return httpService.putJSON(url, {
      agreedToExternalSupportAgreement: version,
    });
  }

  private getSelectedFilter(filterVal: string): string {
    let selFilter = 'active,inactive';
    const vals = filterVal.split('&').filter((v) => v !== '');
    if (vals.length === 1) {
      selFilter = vals[0] === 'active0' ? 'active' : 'inactive';
    }
    return selFilter;
  }

  public findUsers(
    providerId: number,
    pageSize: number,
    page: number,
    filter: string,
    search: string,
    sortModel: SortModel | null,
    includeRoles: boolean = false,
  ): Observable<PagedList<User>> {
    const s = encodeURI(search);
    const f = this.getSelectedFilter(filter);
    const active = f ? `&userAccountState=${f}` : '';
    let url = `${this.apiBase}?providerID=${providerId}&page=${page}&pageSize=${pageSize}&search=${s}&includeRoles=${includeRoles}&isPasswordPermanent=true${active}`;
    if (sortModel) {
      url += `&sort=${
        sortModel.column
      }&sortOrder=${sortModel.direction?.toUpperCase()}`;
    }
    return httpService.getJSON(url);
  }

  public findOnboardingUsers(providerId: number): Observable<PagedList<User>> {
    let url = `${this.apiBase}?providerID=${providerId}&pageSize=1000&isPasswordTemporary=true&includeRoles=true&userAccountState=active,inactive`;
    return httpService.getJSON(url);
  }

  public getUserRoles(
    categoryType: number,
    appTitle?: number,
  ): Observable<UserRole[]> {
    let url = `${this.apiBase}/roles?userCategoryTypeID=${categoryType}`;
    if (appTitle) {
      url += `&ApplicationTitle=${appTitle}`;
    }
    return httpService.getJSON(url);
  }

  public getUser(id: number): Observable<User> {
    return httpService.getJSON(`${this.apiBase}/${id}`);
  }

  public createUser(user: User): Observable<User> {
    return httpService.postJSON(this.apiBase, user);
  }

  public updateUser(user: User): Observable<User> {
    const url = `${this.apiBase}/${user.userAccountID}`;
    return httpService.putJSON(url, user);
  }
  public updateDriverUser(
    userPayload: any,
    userAccountID: Number,
  ): Observable<User> {
    const url = `${this.apiBase}/${userAccountID}`;
    return httpService.putJSON(url, userPayload);
  }
  public onboardUser(
    firstName: string,
    lastName: string,
    email: string,
    providerId?: number,
    providerName?: string,
  ) {
    const url = `${this.apiBase}/onboard`;
    const userInfo = {
      firstName: firstName,
      lastName: lastName,
      emailAddress: email,
      transportationProviderID: providerId || undefined,
      transportationProviderName: providerName,
    };
    return httpService.postJSON(url, userInfo);
  }

  public deleteUser(userId: number): Observable<void> {
    const url = `${this.apiBase}/${userId}`;
    return httpService.deleteJSON(url);
  }

  public sendActivationEmail(userId: number): Observable<void> {
    const url = `${this.apiBase}/${userId}/resend-cognito-activation-email`;
    return httpService.postJSON(url, {});
  }

  public resetPassword(userId: number): Observable<void> {
    const url = `${this.apiBase}/${userId}/reset-cognito-password`;
    return httpService.postJSON(url, {});
  }

  public forgotPassword(email: string): Observable<void> {
    const url = `${this.apiBase}/forgot-cognito-password`;
    return httpService.postJSON(url, { email });
  }

  public magicLink(magicLinkToken: string): Observable<void> {
    const url = `${this.apiBase}/authMagicLink`;
    return httpService.postJSON(url, { magicLinkToken });
  }

  public globalSignout(): Observable<void> {
    const url = `${this.apiBase}/signout`;
    return httpService.getJSON(url);
  }
}

const userService = new UserService();

export default userService;
